import { useCallback, useEffect, useMemo, useState } from 'react';
// COMPONENTS
import LoadingWrapper from 'components/LoadingWrapper';
import intl from 'i18n/intl';
import _ from 'lodash';
import { userSelector } from 'slices/auth.slice';
import { fetchLanguages, languagesSelector } from 'slices/language.slice';
// REDUX
import {
  fetchOrganization,
  organizationLoadingSelector,
  organizationSelector,
  saveOrganization,
} from 'slices/organization.slice';
import { fetchOrganizationHierarchy } from 'slices/organizationHierarchy.slice';
import { fetchRoles } from 'slices/roles.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import { DefaultContentLayout } from '@timeedit/ui-components';
import { Button } from '@timeedit/ui-components/antd';
import { GeneralSettingsForm } from './components/GeneralSettingsForm';
// TYPES
import './index.scss';

const language: any = intl.messages;

const GeneralSettings = () => {
  const dispatch = useAppDispatch();
  const authUser = useAppSelector(userSelector);
  const storeOrganization = useAppSelector(organizationSelector);
  const allLanguages = useAppSelector(languagesSelector);
  const organizationLoading = useAppSelector(organizationLoadingSelector);

  const [localOrganization, setLocalOrganization] = useState(storeOrganization);

  const isEditable = useMemo(() => {
    return authUser?.scopes?.includes('TE_ROOT_USER') ?? false;
  }, [authUser]);

  /**
   * EFFECTS
   */
  useEffect(() => {
    dispatch(fetchOrganization());
    dispatch(fetchOrganizationHierarchy());
    dispatch(fetchRoles());
    dispatch(fetchLanguages());
  }, [dispatch]);

  useEffect(() => {
    setLocalOrganization(storeOrganization);
  }, [storeOrganization]);

  /**
   * EVENT HANDLERS
   */
  const onSaveChanges = useCallback(() => {
    dispatch(saveOrganization(localOrganization));
  }, [dispatch, localOrganization]);

  const onDiscardChanges = useCallback(() => {
    setLocalOrganization(storeOrganization);
  }, [storeOrganization]);

  const onChange = useCallback((prop: string, value: any) => {
    setLocalOrganization((prev: any) => ({ ...prev, [prop]: value }));
  }, []);

  const hasChanges = !_.isEqual(localOrganization, storeOrganization);
  const activeOrganization = isEditable ? localOrganization : storeOrganization;

  const actions = isEditable
    ? [
        <Button key="discard_changes" onClick={onDiscardChanges}>
          {language.discard_changes}
        </Button>,
        <Button
          key="save_changes"
          type="primary"
          onClick={onSaveChanges}
          loading={organizationLoading}
          disabled={!hasChanges}
        >
          {language.save_changes}
        </Button>,
      ]
    : [];

  return (
    <DefaultContentLayout
      title={language.system_general_setting_title}
      subtitle={language.system_general_setting_subtitle}
      actions={actions}
    >
      <LoadingWrapper isLoading={!activeOrganization}>
        <div className="organization-page" data-testid="organization-page">
          <div className="content">
            {activeOrganization && (
              <GeneralSettingsForm
                organization={activeOrganization}
                isEditable={isEditable}
                allLanguages={allLanguages}
                onChange={isEditable ? onChange : undefined}
              />
            )}
          </div>
        </div>
      </LoadingWrapper>
    </DefaultContentLayout>
  );
};

export default GeneralSettings;
